import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { generateCourseFromInputs, CourseContent } from '../services/gemini';
import { ProgressPanel } from './ProgressPanel';
import { ProgressReport } from '../types';
import { collection, addDoc, serverTimestamp, query, where, onSnapshot, doc, deleteDoc, setDoc, getDocs, getDoc } from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import { db, storage, handleFirestoreError, OperationType } from '../lib/firebase';
import { UploadCloud, Link as LinkIcon, Plus, Trash2, Loader2, CheckCircle2, LayoutGrid, Settings, Edit2, X, Save, AlertCircle, FileText, Globe, FileCode, XCircle, Lock, Shapes, Rocket, Zap, Eye, Monitor, Activity, Users, BookOpen, Download, Info } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { createBackgroundJob, shouldPreferBackendProcessing, renderPdfPageToImage } from '../services/gemini';
import { toArray, isMobileDevice, getFriendlyErrorMessage, safeStringify, exportToCsv } from '../lib/utils';
import { Diagnostics, GoldenPathStep } from '../lib/diagnostics';
import { validateDocument } from '../lib/documentValidation';

import { logAppError } from '../lib/errorLogger';

interface Course {
  id: string;
  tutorId: string;
  tutorName: string;
  title: string;
  description: string;
  price: number;
  visibility: 'private' | 'organisation' | 'public';
  orgId?: string;
  likesCount: number;
  commentsCount: number;
  createdAt: any;
  levels?: any;
  sources?: {
    urls: any[];
    pdfs: any[];
  };
}

import { useDocuments } from '../contexts/DocumentContext';
import { useUpdate } from '../contexts/UpdateContext';

export function CourseCreator() {
  const { user, userIntent, memberships, currentOrg } = useAuth();
  const { addDocument } = useDocuments();
  const { setBusyState } = useUpdate();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'create' | 'manage'>('create');
  
  // State Definitions
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState(0);
  const [visibility, setVisibility] = useState<'private' | 'organisation' | 'public'>('private');
  const [progressionMode, setProgressionMode] = useState<'flexible' | 'locked'>('flexible');
  const [targetOrgId, setTargetOrgId] = useState<string>('');
  const [urls, setUrls] = useState<string[]>(['']);
  const [pdfs, setPdfs] = useState<{ name: string; data: string; mimeType: string }[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isConvertingPdf, setIsConvertingPdf] = useState(false);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<any>(null);
  const [progressReports, setProgressReports] = useState<ProgressReport[]>([]);
  const [error, setError] = useState('');
  const [generatedCourse, setGeneratedCourse] = useState<any | null>(null);
  const [generatedBrief, setGeneratedBrief] = useState<any | null>(null);
  const [regeneratingCourseId, setRegeneratingCourseId] = useState<string | null>(null);
  const [isPublishing, setIsPublishing] = useState(false);

  useEffect(() => {
    const isBusy = isGenerating || isPublishing || isConvertingPdf || !!activeJobId || !!generatedCourse || title.length > 5 || description.length > 10;
    setBusyState('course_creator', isBusy);
    return () => setBusyState('course_creator', false);
  }, [isGenerating, isPublishing, isConvertingPdf, activeJobId, generatedCourse, title, description, setBusyState]);

  const [fromDocumentId, setFromDocumentId] = useState<string | null>(null);
  const [existingSources, setExistingSources] = useState<any[]>([]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const docId = params.get('documentId');
    const briefId = params.get('briefId');
    const sourceSetJobId = params.get('sourceSetJobId');
    const tab = params.get('tab');
    
    if (tab === 'manage') {
       setActiveTab('manage');
    } else if (tab === 'create') {
       setActiveTab('create');
    }

    if (sourceSetJobId && user) {
        setActiveTab('create');
        setIsGenerating(true);
        setActiveJobId(sourceSetJobId);

        getDoc(doc(db, 'jobs', sourceSetJobId)).then((snap) => {
            if (!snap.exists()) {
                setError('The Source Set learning job was not found.');
                setIsGenerating(false);
                setActiveJobId(null);
                return;
            }

            const job = snap.data() as any;

            if (job.userId && job.userId !== user.uid) {
                setError('You do not have access to this Source Set learning job.');
                setIsGenerating(false);
                setActiveJobId(null);
                return;
            }

            setTitle(job.title || '');
            setDescription(job.description || '');
            setVisibility(job.visibility || 'private');
            setProgressionMode(job.progressionMode || 'flexible');

            if (job.visibility === 'organisation' && job.organisationId) {
                setTargetOrgId(job.organisationId);
            }

            if (job.status === 'review_ready' && (job.result || job.levels)) {
                const result = job.result || job.levels;
                setGeneratedCourse(result);
                if (job.draftId) {
                    setActiveDraftId(job.draftId);
                }
                setIsGenerating(false);
                setActiveJobId(null);
            }
        }).catch((err) => {
            console.error('Failed to load Source Set learning job:', err);
            setError(err?.message || 'Failed to load Source Set learning job.');
            setIsGenerating(false);
            setActiveJobId(null);
        });
    }

    if (briefId && user) {
        getDoc(doc(db, 'documentBriefs', briefId)).then((snap) => {
           if (snap.exists() && snap.data().userId === user.uid) {
              setGeneratedBrief(snap.data());
           }
        }).catch(console.error);
    }

    if (docId && user) {
       getDoc(doc(db, 'documents', docId)).then((snap) => {
          if (snap.exists() && snap.data().userId === user.uid) {
             const data = snap.data();
             setTitle(data.title);
             setFromDocumentId(docId);
             setExistingSources(data.sources || []);
          }
       }).catch(console.error);
    }
  }, [location.search, user]);

  const handleSwitchToCreate = () => {
    setActiveTab('create');
    setTitle('');
    setDescription('');
    setPrice(0);
    setUrls(['']);
    setPdfs([]);
    setGeneratedCourse(null);
    setRegeneratingCourseId(null);
    setActiveJobId(null);
    setJobStatus(null);
    setEditingCourse(null);
    setViewingProgressCourse(null);
  };

  const loadCourseProgress = (course: Course) => {
    setViewingProgressCourse(course);
    setProgressLoading(true);
    setLearnerProgress([]);
    
    // Admins viewing org courses shouldn't see it this way?
    // The instructions say "Organisation admin can view progress for organisation learners... inside their organisation. Tutor can view their own." 
    // Here we just fetch enrollments with `courseId`, and Firestore rules should restrict if needed.
    const q = query(collection(db, 'enrollments'), where('courseId', '==', course.id));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setLearnerProgress(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setProgressLoading(false);
    }, (error) => {
      console.error('Error fetching course progress:', error);
      setProgressLoading(false);
    });
    // For MVP, we don't store unsubscribe, just let it run or they refresh page. 
    // Or we can just do getDocs for a one-off fetch.
  };

  const loadCourseProgressOneOff = async (course: Course) => {
    setViewingProgressCourse(course);
    setProgressLoading(true);
    setLearnerProgress([]);
    try {
      const q = query(collection(db, 'enrollments'), where('courseId', '==', course.id));
      const snaps = await getDocs(q);
      setLearnerProgress(snaps.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch(err) {
      console.error(err);
    } finally {
      setProgressLoading(false);
    }
  };
  
  // Create Course State already defined above

  // Manage Courses State
  const [activeDraftId, setActiveDraftId] = useState<string | null>(null);
  const [myCourses, setMyCourses] = useState<Course[]>([]);
  const [viewingProgressCourse, setViewingProgressCourse] = useState<Course | null>(null);
  const [learnerProgress, setLearnerProgress] = useState<any[]>([]);
  const [progressLoading, setProgressLoading] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPrice, setEditPrice] = useState(0);
  const [editUrls, setEditUrls] = useState<string[]>([]);
  const [editPdfs, setEditPdfs] = useState<{ name: string; data: string; mimeType: string }[]>([]);
  const [editVisibility, setEditVisibility] = useState<'private' | 'organisation' | 'public'>('public');
  const [editOrgId, setEditOrgId] = useState<string>('');

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  // Poll for job status
  useEffect(() => {
    if (!activeJobId) return;

    const unsubscribe = onSnapshot(doc(db, 'jobs', activeJobId), (snapshot) => {
      if (!snapshot.exists()) return;
      const data = snapshot.data();
      setJobStatus(data);

      if (data.status === 'published' && data.intent === 'brief') {
        setIsGenerating(false);
        setActiveJobId(null);
        
        if (data.briefId) {
            getDoc(doc(db, 'documentBriefs', data.briefId)).then(snapshot => {
                if (snapshot.exists()) {
                    setGeneratedBrief(snapshot.data());
                }
            });
        }
      }

      if (data.status === 'completed' && data.intent === 'brief' && data.briefId) {
        setIsGenerating(false);
        setActiveJobId(null);
        getDoc(doc(db, 'documentBriefs', data.briefId)).then(snapshot => {
          if (snapshot.exists()) {
            setGeneratedBrief(snapshot.data());
          }
        });
      }

      if (data.status === 'review_ready' || (data.status === 'completed' && (data.result || data.levels))) {
        console.log(`[Job ${activeJobId}] REVIEW READY. Preparing preview...`);
        
        // The job now returns the full course object in 'result'
        const result = data.result || data.levels;
        
        // PART 2: Validate structure before rendering
        if (result && result.levels) {
           const hasLessons = ['explorer', 'apprentice', 'master'].some(lvl => 
             result.levels[lvl]?.lessons?.length > 0 || result[lvl]?.lessons?.length > 0
           );
           
           if (!hasLessons) {
              console.error(`[Job ${activeJobId}] Validation failed: No lessons found in result.`);
              setError("Course generation returned no lessons. Please try again with different settings or sources.");
              setIsGenerating(false);
              setActiveJobId(null);
              return;
           }

          setGeneratedCourse(result);
          if (data.courseId) {
            setRegeneratingCourseId(data.courseId);
          }
          if (data.draftId) {
            setActiveDraftId(data.draftId);
            Diagnostics.logStep(GoldenPathStep.DRAFT_CREATED, 'success', { draftId: data.draftId });
          }
          if (data.briefId) {
             getDoc(doc(db, 'documentBriefs', data.briefId)).then(snapshot => {
                 if (snapshot.exists()) {
                     setGeneratedBrief(snapshot.data());
                 }
             });
          }

          setIsGenerating(false);
          setActiveJobId(null);
          Diagnostics.logStep(GoldenPathStep.PREVIEW_READY, 'success');
        } else {
          setError("Job completed but no content was returned. Please try again.");
          setIsGenerating(false);
          setActiveJobId(null);
          Diagnostics.logStep(GoldenPathStep.COURSE_GENERATE, 'fail', { error: 'No content returned' });
        }
      }

      const isTerminalStatus = ['failed', 'timed_out', 'cancelled', 'completed', 'review_ready'].includes(data.status);
      const heartbeatAgeMs = data.lastHeartbeatAt?.toDate ? Date.now() - data.lastHeartbeatAt.toDate().getTime() : null;
      const isStalled = !isTerminalStatus && heartbeatAgeMs !== null && heartbeatAgeMs > 180000;

      if (isStalled) {
        setError('Job appears stalled. The backend stopped updating this job, so it may need a retry.');
        setIsGenerating(false);
        setActiveJobId(null);
        return;
      }

      if (['failed', 'timed_out', 'cancelled'].includes(data.status)) {
        const errorMessage = data.errorMessage || data.error || data.message || 'Generation failed.';
        setError(errorMessage);
        setIsGenerating(false);
        setActiveJobId(null);
      }

      if (data.status === 'failed') {
        setIsGenerating(false);
        setActiveJobId(null);
        setError(getFriendlyErrorMessage(data.error || 'Background processing failed.'));
        Diagnostics.logStep(GoldenPathStep.COURSE_GENERATE, 'fail', { error: data.error });
      }
    }, (error) => {
      console.error("Job listener error:", error);
      setError(`Permission Error: Please ensure you are logged in correctly. (Path: jobs/${activeJobId})`);
    });

    return () => unsubscribe();
  }, [activeJobId, navigate]);

  useEffect(() => {
    if (currentOrg) {
      setTargetOrgId(currentOrg.id);
    }
  }, [currentOrg]);

  useEffect(() => {
    if (!user || activeTab !== 'manage') return;

    const q = query(collection(db, 'courses'), where('tutorId', '==', user.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const coursesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Course));
      coursesData.sort((a, b) => {
        const dateA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const dateB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return dateB - dateA;
      });
      setMyCourses(coursesData);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'courses');
    });

    return () => unsubscribe();
  }, [user, activeTab]);

  const isMobile = isMobileDevice();
  const [bypassMobile, setBypassMobile] = useState(false);

  useEffect(() => {
    if (isMobile) {
      console.log('Mobile creation access blocked (can be bypassed)');
    } else {
      console.log('Desktop creation access allowed');
    }
  }, [isMobile]);

  if (isMobile && !bypassMobile) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] p-4 text-center bg-bg-main text-text-main">
        <div className="bg-bg-alt border border-rose-500/20 p-8 rounded-3xl max-w-md shadow-2xl">
          <div className="h-16 w-16 bg-rose-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Settings className="h-8 w-8 text-rose-500" />
          </div>
          <h2 className="text-2xl font-bold text-text-main mb-3">Desktop Required</h2>
          <p className="text-text-alt mb-6 leading-relaxed">
            Course creation works best on desktop. Please use a desktop browser to upload documents and generate or regenerate courses. You can still use mobile to learn, listen, ask the tutor, take quizzes, and continue your progress.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                alert('Link copied! Open this link on your desktop browser.');
              }}
              className="w-full py-3 bg-bg-main border border-border-main hover:bg-bg-alt text-text-main font-bold rounded-xl transition-all"
            >
              Copy Desktop Link
            </button>
            <button
              onClick={() => setBypassMobile(true)}
              className="w-full py-3 bg-indigo-600/10 hover:bg-indigo-600/20 text-indigo-500 font-bold rounded-xl transition-all"
            >
              Continue Anyway
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-all"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Manage Courses State

  const handleAddUrl = () => setUrls([...urls, '']);
  const handleRemoveUrl = (index: number) => setUrls(urls.filter((_, i) => i !== index));
  const handleUrlChange = (index: number, value: string) => {
    const newUrls = [...urls];
    newUrls[index] = value;
    setUrls(newUrls);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    for (const file of Array.from(files)) {
      const reader = new FileReader();
      reader.onerror = () => {
        Diagnostics.logStep(GoldenPathStep.DOCUMENT_UPLOAD, 'fail', { name: file.name, type: file.type, error_type: 'FILE_READ_ERROR' });
        setError("We could not read this file from your device. This can happen with iCloud files, scanned PDFs, unsupported file types, or browser memory limits. Please try downloading the file locally first, using a smaller readable file, or uploading from a desktop browser.");
        logAppError('upload_pdf_read_error', 'FILE_READ_ERROR for ' + file.name, { userId: user?.uid, userEmail: user?.email, route: '/courses/manage' });
      };
      reader.onload = async (event) => {
        const base64String = (event.target?.result as string).split(',')[1];
        
        setIsGenerating(true); // temporary loading state for validation & upload
        const validation = await validateDocument(file, base64String);

        if (!validation.isValid) {
            setIsGenerating(false);
            setError(validation.error || "File invalid");
            logAppError('upload_pdf_validation_failed', validation.error || "File invalid", { userId: user?.uid, userEmail: user?.email, route: '/courses/manage' });
            return;
        }

        const sourceId = `src-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
        const safeFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const storagePath = currentOrg?.id 
           ? `organisations/${currentOrg.id}/users/${user?.uid || 'unknown'}/sources/${sourceId}/${safeFileName}`
           : `users/${user?.uid || 'unknown'}/sources/${sourceId}/${safeFileName}`;

        try {
           const storageRef = ref(storage, storagePath);
           await uploadBytes(storageRef, file);
           
           // Create source metadata
           await setDoc(doc(db, 'sources', sourceId), {
               sourceId,
               userId: user?.uid,
               organisationId: currentOrg?.id || null,
               fileName: file.name,
               safeFileName,
               fileType: file.name.toLowerCase().endsWith('.docx') || file.type.includes('wordprocessingml') ? 'word' : 'pdf',
               mimeType: file.type,
               fileSize: file.size,
               storagePath,
               status: 'uploaded',
               createdAt: serverTimestamp(),
               updatedAt: serverTimestamp(),
               uploadedBy: user?.email || 'unknown',
           });

           const docId = `doc-${Date.now()}-${file.name}`;
           
           // We intentionally do not store data inside pdfs state to avoid Base64 Bloat sending to API
           setPdfs(prev => [...prev, { 
             name: file.name, 
             data: '', 
             mimeType: file.type,
             id: docId,
             storagePath,
             sourceId
           } as any]);

           setError(''); // Clear errors on success
        } catch(uploadErr: any) {
           console.error("Upload failed", uploadErr);
           setError("Upload failed. Please check your connection or try a smaller file.");
           logAppError('storage_upload_failed', uploadErr.message, { userId: user?.uid, userEmail: user?.email, route: '/courses/manage' });
        } finally {
           setIsGenerating(false);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePdf = (index: number) => {
    setPdfs(pdfs.filter((_, i) => i !== index));
  };

  const handleAddEditUrl = () => setEditUrls([...editUrls, '']);
  const handleRemoveEditUrl = (index: number) => setEditUrls(editUrls.filter((_, i) => i !== index));
  const handleEditUrlChange = (index: number, value: string) => {
    const newUrls = [...editUrls];
    newUrls[index] = value;
    setEditUrls(newUrls);
  };

  const handleEditFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    for (const file of Array.from(files)) {
      const reader = new FileReader();
      reader.onerror = () => {
        Diagnostics.logStep(GoldenPathStep.DOCUMENT_UPLOAD, 'fail', { name: file.name, type: file.type, error_type: 'FILE_READ_ERROR' });
        setError("We could not read this file from your device. This can happen with iCloud files, scanned PDFs, unsupported file types, or browser memory limits. Please try downloading the file locally first, using a smaller readable file, or uploading from a desktop browser.");
        logAppError('upload_pdf_read_error', 'FILE_READ_ERROR for ' + file.name, { userId: user?.uid, userEmail: user?.email, route: '/courses/manage' });
      };
      reader.onload = async (event) => {
        const base64String = (event.target?.result as string).split(',')[1];
        
        setIsGenerating(true);
        const validation = await validateDocument(file, base64String);

        if (!validation.isValid) {
            setIsGenerating(false);
            setError(validation.error || "File invalid");
            logAppError('upload_pdf_validation_failed', validation.error || "File invalid", { userId: user?.uid, userEmail: user?.email, route: '/courses/manage' });
            return;
        }

        const sourceId = `src-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
        const safeFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const storagePath = currentOrg?.id 
           ? `organisations/${currentOrg.id}/users/${user?.uid || 'unknown'}/sources/${sourceId}/${safeFileName}`
           : `users/${user?.uid || 'unknown'}/sources/${sourceId}/${safeFileName}`;

        try {
           const storageRef = ref(storage, storagePath);
           await uploadBytes(storageRef, file);
           
           await setDoc(doc(db, 'sources', sourceId), {
               sourceId,
               userId: user?.uid,
               organisationId: currentOrg?.id || null,
               fileName: file.name,
               safeFileName,
               fileType: file.name.toLowerCase().endsWith('.docx') || file.type.includes('wordprocessingml') ? 'word' : 'pdf',
               mimeType: file.type,
               fileSize: file.size,
               storagePath,
               status: 'uploaded',
               createdAt: serverTimestamp(),
               updatedAt: serverTimestamp(),
               uploadedBy: user?.email || 'unknown',
           });

           setEditPdfs(prev => [...prev, { 
             name: file.name, 
             data: '', 
             mimeType: file.type,
             id: `doc-${Date.now()}-${file.name}`,
             storagePath,
             sourceId
           } as any]);

           setError('');
        } catch(uploadErr: any) {
           console.error("Upload failed", uploadErr);
           setError("Upload failed. Please check your connection or try a smaller file.");
        } finally {
           setIsGenerating(false);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveEditPdf = (index: number) => {
    setEditPdfs(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (intent: 'course' | 'brief' | 'both', e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!user) return;
    if (!title.trim()) {
      setError('Please provide a course title.');
      return;
    }

    const validUrls = urls.filter(u => u.trim() !== '');
    if (validUrls.length === 0 && pdfs.length === 0 && !fromDocumentId) {
      setError('Please upload a readable PDF, Word document, or add a valid web link before generating a course.');
      return;
    }

    if (validUrls.length > 1) {
      setError('Maximum 1 URL allowed per course generation for now.');
      return;
    }

    if (pdfs.length + validUrls.length > 3 && !fromDocumentId) {
      setError('Maximum 3 sources allowed per course generation.');
      return;
    }

    Diagnostics.logStep(GoldenPathStep.CREATE_COURSE_START, 'start', { title, hasPdf: pdfs.length > 0, hasUrls: validUrls.length > 0, intent, fromDocumentId });
    if (pdfs.length > 0) {
        Diagnostics.logStep(GoldenPathStep.DOCUMENT_UPLOAD, 'success', { pdfCount: pdfs.length });
    }

    setIsGenerating(true);
    setProgressReports([]);
    setError('');
    // Clear any previous generated course state from frontend memory
    setGeneratedCourse(null);
    setRegeneratingCourseId(null);
    setActiveJobId(null);
    setJobStatus(null);
    setGeneratedBrief(null);

    const sessionId = `req-${Date.now()}`;
    console.log(`[Before Generation] Session ID: ${sessionId}`);
    console.log(`[Before Generation] Source Names:`, pdfs.map(p => p.name).concat(validUrls));
    console.log(`[Before Generation] Source Count: ${pdfs.length + validUrls.length}`);
    console.log(`[Before Generation] Source File Sizes:`, pdfs.map(p => p.data.length));
    console.log(`[Before Generation] Source Types:`, pdfs.map(p => p.mimeType).concat(validUrls.map(u => 'url')));

    try {
      // Decide if we should use background processing
      const useBackend = !!fromDocumentId || intent === 'brief' || intent === 'both' || shouldPreferBackendProcessing(pdfs);

      if (useBackend) {
        // Backend processing path
        const sources = fromDocumentId && pdfs.length === 0 && validUrls.length === 0 
          ? existingSources 
          : [
          ...pdfs.map((p, index) => ({ 
            id: (p as any).id || `pdf-${index}`, 
            name: p.name, 
            type: p.name.toLowerCase().endsWith('.docx') || p.mimeType?.includes('wordprocessingml') ? 'word' : 'pdf', 
            data: p.data, // This is empty now!
            storagePath: (p as any).storagePath,
            sourceId: (p as any).sourceId
          })),
          ...validUrls.map(u => ({ 
            id: `url-${Date.now()}-${u.slice(0, 10)}`, 
            name: u, 
            type: 'url', 
            url: u 
          }))
        ];

        const { jobId } = await createBackgroundJob(title, sources, user.uid, undefined, intent, fromDocumentId || undefined);
        setActiveJobId(jobId);
        // Note: polling is handled by useEffect
        return;
      }

      // Frontend processing path (for small files on desktop)
      const courseContent: CourseContent = await generateCourseFromInputs(title, pdfs, validUrls, (report) => {
        setProgressReports(prev => {
          // Update details for the same stage if it exists, otherwise add new
          const last = prev[prev.length - 1];
          if (last && last.stage === report.stage) {
            return [...prev.slice(0, -1), report];
          }
          return [...prev, report];
        });
      }, sessionId);
      setGeneratedCourse(courseContent);
      
      if (courseContent.generationMetadata?.isPartial) {
        // We'll show a warning but let them continue
        console.warn(`${courseContent.generationMetadata.failedLessonsCount} lessons failed to generate.`);
      }
    } catch (err: any) {
      console.error(err);
      if (err.message.includes('SYSTEM_BUSY')) {
        setError('The system is currently under heavy load. Processing will begin shortly, please try again in a few moments.');
      } else if (err.message.includes('USER_LIMIT')) {
        setError('You have too many active generation tasks. Please wait for them to finish before starting a new one.');
      } else if (err.message.includes('REAL_GEMINI_API_KEY_MISSING')) {
        setError('System configuration error: AI service is not connected. Please contact admin.');
      } else {
        setError(err.message || 'Failed to generate course. Please try again.');
      }
      logAppError('generate_course', err.message, { userId: user?.uid, userEmail: user?.email, route: '/courses/manage' });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApprove = async () => {
    if (!user || !generatedCourse) return;
    setIsPublishing(true);
    setError('');

    Diagnostics.logStep(GoldenPathStep.COURSE_REVIEW, 'success');

    try {
      let finalCourseId = regeneratingCourseId;
      
      // CRITICAL: "Bake" visual assets by rendering PDF pages to base64 images
      // This ensures that students who don't have the original PDFs can still see the visuals.
      const bakedCourse = JSON.parse(safeStringify(generatedCourse));
      const pdfMap = new Map(pdfs.map(p => [p.name, p.data]));

      const bakeVisuals = async (lessons: any[]) => {
        if (!lessons) return;
        for (const lesson of lessons) {
          if (lesson.visuals && Array.isArray(lesson.visuals)) {
            for (const visual of lesson.visuals) {
              if (!visual.image_data && visual.source_doc_id && visual.page_number) {
                const pdfData = pdfMap.get(visual.source_doc_id);
                if (pdfData && pdfData.length > 0) {
                  console.log(`[Publishing] Baking visual for ${visual.source_doc_id} Page ${visual.page_number}`);
                  const imageData = await renderPdfPageToImage(pdfData, visual.page_number);
                  if (imageData) visual.image_data = imageData;
                } else {
                  console.log(`[Publishing] Skipping baking visual for ${visual.source_doc_id} Page ${visual.page_number} because file was uploaded directly to cloud.`);
                }
              }
            }
          }
        }
      };

      await bakeVisuals(bakedCourse.levels?.explorer?.lessons);
      await bakeVisuals(bakedCourse.levels?.apprentice?.lessons);
      await bakeVisuals(bakedCourse.levels?.master?.lessons);

      const courseData = {
        ...bakedCourse,
        tutorId: user.uid,
        tutorName: user.displayName || 'Unknown Tutor',
        // If it's a new course, we use the current UI state for metadata
        // If it's a regeneration, we use what's in bakedCourse (which merged existing + new)
        title: regeneratingCourseId ? (bakedCourse.title || title) : title,
        description: regeneratingCourseId ? (bakedCourse.description || description) : description,
        price: regeneratingCourseId ? (bakedCourse.price ?? price) : price,
        visibility: regeneratingCourseId ? (bakedCourse.visibility || visibility) : visibility,
        progressionMode: regeneratingCourseId ? (bakedCourse.progressionMode || progressionMode) : progressionMode,
        orgId: regeneratingCourseId 
          ? (bakedCourse.orgId || editOrgId || (memberships && memberships.length > 0 ? memberships[0].orgId : null)) 
          : (visibility === 'organisation' ? (targetOrgId || (memberships && memberships.length > 0 ? memberships[0].orgId : null)) : null),
        updatedAt: serverTimestamp(),
        // Ensure some fields are definitely initialized for new courses
        createdAt: bakedCourse.createdAt || serverTimestamp(),
        likesCount: bakedCourse.likesCount || 0,
        commentsCount: bakedCourse.commentsCount || 0,
        // CRITICAL: Ensure visuals are properly mapped from lessons if they exist
        visual_assets: toArray(bakedCourse.visual_assets || [])
      };

      // MVP PRE-PUBLISH VALIDATION
      const getLessons = (crs: any) => {
         const explorer = crs.levels?.explorer;
         return Array.isArray(explorer) ? explorer : (explorer?.lessons || []);
      };

      const getSources = (crs: any) => {
          return crs.source_excerpts?.map((s: any) => s.document_name || '').join(',') || '';
      };

      const draftLessons = getLessons(generatedCourse);
      const outputLessons = getLessons(courseData);

      if (draftLessons.length !== outputLessons.length) {
         throw new Error("Publishing failed because the published course did not match the approved draft.");
      }
      
      if (draftLessons.length > 0 && outputLessons.length > 0) {
         if (draftLessons[0].title !== outputLessons[0].title) {
            throw new Error("Publishing failed because the published course did not match the approved draft.");
         }
         // content preview match (just check first 20 chars of content or so, or exact match)
         const draftPreview = (draftLessons[0].content || '').substring(0, 50);
         const outputPreview = (outputLessons[0].content || '').substring(0, 50);
         if (draftPreview !== outputPreview) {
            throw new Error("Publishing failed because the published course did not match the approved draft.");
         }
      }

      if (getSources(generatedCourse) !== getSources(courseData)) {
          throw new Error("Publishing failed because the published course did not match the approved draft.");
      }

      if (regeneratingCourseId) {
        console.log(`[Regeneration] User APPROVED. Publishing update to courses/${regeneratingCourseId}`);
        Diagnostics.logStep(GoldenPathStep.DRAFT_APPROVED, 'success', { courseId: regeneratingCourseId, draftId: activeDraftId });
        await setDoc(doc(db, 'courses', regeneratingCourseId), courseData, { merge: true });
        Diagnostics.logStep(GoldenPathStep.COURSE_REPUBLISHED, 'success', { courseId: regeneratingCourseId });
      } else {
        console.log(`[Creation] User APPROVED. Publishing new course.`);
        Diagnostics.logStep(GoldenPathStep.DRAFT_APPROVED, 'success', { draftId: activeDraftId });
        const docRef = await addDoc(collection(db, 'courses'), courseData);
        finalCourseId = docRef.id;
        Diagnostics.logStep(GoldenPathStep.COURSE_PUBLISHED, 'success', { courseId: finalCourseId });
      }

      if (finalCourseId) {
        if (activeDraftId) {
          try {
             await setDoc(doc(db, 'drafts', activeDraftId), { status: 'published' }, { merge: true });
             await setDoc(doc(db, 'jobs', activeDraftId), { status: 'published' }, { merge: true });
          } catch(e) { }
          setActiveDraftId(null);
        }
        // Clear form inputs manually
        setTitle('');
        setDescription('');
        setUrls(['']);
        setPdfs([]);
        setGeneratedCourse(null);
        setRegeneratingCourseId(null);
        navigate(`/course/${finalCourseId}`);
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Failed to publish course.');
      Diagnostics.logStep(GoldenPathStep.COURSE_PUBLISHED, 'fail', { error: err.message });
      handleFirestoreError(err, OperationType.UPDATE, regeneratingCourseId ? `courses/${regeneratingCourseId}` : 'courses');
      logAppError('publish_course', err.message, { userId: user?.uid, userEmail: user?.email, route: '/courses/manage', organisationId: targetOrgId });
    } finally {
      setIsPublishing(false);
    }
  };

  const handleReject = async () => {
    console.log(`[Regeneration] User DISCARDED changes.`);
    if (activeDraftId) {
       try {
           await setDoc(doc(db, 'drafts', activeDraftId), { status: 'cancelled' }, { merge: true });
           await setDoc(doc(db, 'jobs', activeDraftId), { status: 'cancelled' }, { merge: true });
       } catch(e) { }
       setActiveDraftId(null);
    }
    setGeneratedCourse(null);
    setRegeneratingCourseId(null);
    if (activeTab === 'manage') {
      setEditingCourse(null);
    } else {
      // Clear form inputs
      setTitle('');
      setDescription('');
      setUrls(['']);
      setPdfs([]);
    }
  };

  const handleDeleteCourse = (courseId: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Course',
      message: 'Are you sure you want to delete this course forever? This action cannot be undone and will remove it from the Course Library.',
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'courses', courseId));
        } catch (error) {
          handleFirestoreError(error, OperationType.DELETE, `courses/${courseId}`);
        }
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const handleStartEdit = (course: Course) => {
    // Clear generation state to avoid mixing review with edit
    setGeneratedCourse(null);
    setActiveJobId(null);
    setJobStatus(null);
    setError('');
    
    setEditingCourse(course);
    setEditTitle(course.title || '');
    setEditDescription(course.description || '');
    setEditPrice(course.price || 0);
    setEditVisibility(course.visibility || 'public');
    setEditOrgId(course.orgId || (course.visibility === 'organisation' && memberships.length > 0 ? memberships[0].orgId : ''));
    
    // Support both string array (legacy) and object array (new)
    const rawUrls = course.sources?.urls || [];
    setEditUrls(rawUrls.map((u: any) => typeof u === 'string' ? u : (u.url || u.id)));
    
    setEditPdfs([]); 
  };

  const handleRegenerateCourse = async () => {
    if (!user || !editingCourse) return;
    
    // Validate that we have at least some sources (new or existing)
    const validUrls = editUrls.filter(u => u.trim() !== '');
    const existingPdfs = editingCourse.sources?.pdfs || [];
    
    if (validUrls.length === 0 && editPdfs.length === 0 && existingPdfs.length === 0) {
        setError('Please upload a readable PDF, Word document, or add a valid web link before generating a course.');
        return;
    }

    if (validUrls.length > 1) {
      setError('Maximum 1 URL allowed per course generation for now.');
      return;
    }

    if (validUrls.length + editPdfs.length + existingPdfs.length > 3) {
      setError('Maximum 3 sources allowed per course generation.');
      return;
    }

    setIsGenerating(true);
    setProgressReports([]);
    setError('');

    try {
        console.log(`[Regeneration] Triggering for Course: ${editingCourse.id}`);
        console.log('[Regeneration] Persisting sources immediately to course.');

        Diagnostics.logStep(GoldenPathStep.REGENERATE_START, 'start', {
          courseId: editingCourse.id,
          sourcesBefore: {
            urls: editingCourse.sources?.urls || [],
            pdfs: editingCourse.sources?.pdfs || []
          }
        });

        const currentSources: any = editingCourse.sources || {};
        const newPdfSources = [...toArray(currentSources.pdfs)];
        
        const existingUrlObjects = toArray(currentSources.urls).map((u: any) => 
          typeof u === 'string' ? { url: u, name: u } : u
        );
        
        const updatedUrls = editUrls.filter(u => u.trim()).map(urlStr => {
          const existing = existingUrlObjects.find((u: any) => u.url === urlStr);
          return existing ? existing : { id: `url-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`, url: urlStr, name: urlStr };
        });

        // Add IDs to new PDFs so we can map them back later
        const editPdfsWithIds = editPdfs.map(p => ({
            ...p,
            id: (p as any).id || `pdf-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`
        }));
        
        if (editPdfsWithIds.length > 0) {
          const newPdfMetadata = editPdfsWithIds.map(p => ({ 
            name: p.name, 
            mimeType: p.mimeType,
            id: p.id,
            isNew: true
          }));
          newPdfSources.push(...newPdfMetadata);
        }

        await setDoc(doc(db, 'courses', editingCourse.id), {
          updatedAt: serverTimestamp(),
          sources: {
            urls: updatedUrls,
            pdfs: newPdfSources
          }
        }, { merge: true });

        // Build sources array for job
        const sources = [
          ...updatedUrls.map((u: any) => ({ 
            id: u.id, 
            name: u.name, 
            type: 'url', 
            url: u.url,
            data: u.url
          })),
          ...editPdfsWithIds.map(p => ({ 
            id: p.id, 
            name: p.name, 
            type: p.name.toLowerCase().endsWith('.docx') || (p as any).mimeType?.includes('wordprocessingml') ? 'word' : 'pdf', 
            data: (p as any).data 
          })),
          ...existingPdfs
            .filter((p: any) => !editPdfsWithIds.some(np => np.name === p.name))
            .map((p: any) => ({
              id: p.id || p.name,
              name: p.name,
              type: p.name.toLowerCase().endsWith('.docx') ? 'word' : 'pdf',
              data: null // Handled in aiRuntime fallback
            }))
        ];

        console.log(`[Regeneration] Sources prep completed. Starting job...`);
        console.log(`[Regeneration] Sources sent to job:`, sources.map(s => `${s.name} (${s.type})`));
        
        Diagnostics.logStep(GoldenPathStep.REGENERATE_START, 'success', {
          jobSources: sources.map(s => ({ id: s.id, type: s.type, name: s.name }))
        });

        const { jobId } = await createBackgroundJob(editTitle, sources, user.uid, editingCourse.id);
        
        console.log(`[Regeneration] Job started:`, jobId);
        setActiveJobId(jobId);
        setEditingCourse(null); 
    } catch (err: any) {
        console.error("Regeneration trigger failed:", err);
        setError(err.message || 'Failed to trigger course regeneration.');
    } finally {
        setIsGenerating(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingCourse) return;
    if (!editTitle.trim()) {
      setError('Course title cannot be empty.');
      return;
    }
    setError('');
    try {
      console.log(`[Edit] Saving metadata for course ${editingCourse.id}`);
      
      const currentSources: any = editingCourse.sources || {};
      const newPdfSources = [...toArray(currentSources.pdfs)];
      
      // 1. URLs: Keep content if URL matches
      const existingUrlObjects = toArray(currentSources.urls).map((u: any) => 
        typeof u === 'string' ? { url: u, name: u } : u
      );
      
      const updatedUrls = editUrls.filter(u => u.trim()).map(urlStr => {
        const existing = existingUrlObjects.find((u: any) => u.url === urlStr);
        return existing ? existing : { url: urlStr, name: urlStr };
      });
      
      // 2. New PDFs (Metadata only, for later regeneration)
      if (editPdfs && editPdfs.length > 0) {
        const newPdfMetadata = editPdfs.map(p => ({ 
          name: p.name, 
          mimeType: p.mimeType,
          id: (p as any).id || `pdf-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
          isNew: true
        }));
        newPdfSources.push(...newPdfMetadata);
      }

      const finalOrgId = editOrgId || (memberships && memberships.length > 0 ? memberships[0].orgId : null);

      const updateData: any = {
        title: editTitle.trim(),
        description: editDescription.trim(),
        price: editPrice,
        visibility: editVisibility,
        orgId: editVisibility === 'organisation' ? finalOrgId : null,
        updatedAt: serverTimestamp(),
        sources: {
          urls: updatedUrls,
          pdfs: newPdfSources
        }
      };

      // Ensure no conflicting dot-notation keys in the root of updateData
      Object.keys(updateData).forEach(key => {
        if (key.includes('.')) delete updateData[key];
      });

      await setDoc(doc(db, 'courses', editingCourse.id), updateData, { merge: true });
      setEditingCourse(null);
      setEditPdfs([]);
      console.log(`[Edit] Success. URLs=${updatedUrls.length}, PDFs=${newPdfSources.length}`);
    } catch (error) {
      console.error("[Edit] Failed to save:", error);
      handleFirestoreError(error, OperationType.UPDATE, `courses/${editingCourse.id}`);
    }
  };

  if (generatedBrief && !activeJobId && !isGenerating) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-in fade-in zoom-in duration-300">
        <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-text-main tracking-tight">Document Brief</h1>
            <p className="mt-2 text-text-alt text-lg">{generatedBrief.documentTitle || title}</p>
          </div>
          <div className="flex gap-3">
             <button
               onClick={() => setGeneratedBrief(null)}
               className="px-4 py-2 text-sm font-medium text-text-alt bg-bg-alt hover:text-text-main border border-border-main hover:border-text-alt/30 rounded-lg transition-colors"
             >
               Close Brief
             </button>
             {generatedCourse ? (
               <button
                 onClick={() => setGeneratedBrief(null)}
                 className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition-colors"
               >
                 View Course
               </button>
             ) : (
               <button
                 onClick={() => handleSubmit('course')}
                 className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition-colors"
               >
                 Create Course
               </button>
             )}
          </div>
        </div>

        <div className="space-y-6">
          {/* Executive Brief */}
          {generatedBrief.executiveBrief && (
            <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/5 p-6 rounded-2xl border border-indigo-500/20 shadow-lg">
              <h2 className="text-sm font-bold text-indigo-400 uppercase tracking-widest mb-3">Executive Summary</h2>
              <p className="text-text-main text-lg leading-relaxed">{generatedBrief.executiveBrief}</p>
              {generatedBrief.shortSummary && (
                 <p className="mt-4 pt-4 border-t border-indigo-500/10 text-text-alt italic">
                   {generatedBrief.shortSummary}
                 </p>
              )}
            </div>
          )}

          {/* Key Points & Action Items */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-bg-alt p-6 rounded-2xl border border-border-main">
              <h2 className="text-sm font-bold text-text-alt uppercase tracking-widest mb-4 flex items-center gap-2">
                <Zap className="h-4 w-4 text-emerald-400" />
                Key Points
              </h2>
              <ul className="space-y-3">
                {toArray(generatedBrief.keyPoints).map((point: string, idx: number) => (
                  <li key={idx} className="flex items-start gap-3 text-text-main">
                    <span className="shrink-0 w-1.5 h-1.5 mt-2 rounded-full bg-emerald-400" />
                    <span className="leading-relaxed">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-bg-alt p-6 rounded-2xl border border-border-main">
              <h2 className="text-sm font-bold text-text-alt uppercase tracking-widest mb-4 flex items-center gap-2">
                <Activity className="h-4 w-4 text-rose-400" />
                Action Items
              </h2>
              <ul className="space-y-3">
                {toArray(generatedBrief.actionItems).map((item: string, idx: number) => (
                  <li key={idx} className="flex items-start gap-3 text-text-main">
                    <span className="shrink-0 w-1.5 h-1.5 mt-2 rounded-full bg-rose-400" />
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Important Terms */}
          {generatedBrief.importantTerms && generatedBrief.importantTerms.length > 0 && (
            <div className="bg-bg-alt p-6 rounded-2xl border border-border-main">
              <h2 className="text-sm font-bold text-text-alt uppercase tracking-widest mb-4 flex items-center gap-2">
                <FileText className="h-4 w-4 text-indigo-400" />
                Important Terms
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {toArray(generatedBrief.importantTerms).map((termInfo: any, idx: number) => (
                  <div key={idx} className="p-3 bg-bg-main/50 rounded-lg border border-border-main/50">
                    <span className="font-bold text-text-main block">{termInfo.term}</span>
                    <span className="text-sm text-text-alt block mt-1">{termInfo.definition}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Suggested Learning Path */}
          {generatedBrief.suggestedLearningPath && generatedBrief.suggestedLearningPath.length > 0 && (
             <div className="bg-bg-alt p-6 rounded-2xl border border-border-main text-center">
               <h2 className="text-sm font-bold text-text-alt uppercase tracking-widest mb-4">Suggested Learning Path</h2>
               <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-text-main">
                  {toArray(generatedBrief.suggestedLearningPath).map((pathInfo: any, idx: number) => (
                     <React.Fragment key={idx}>
                       <span className="font-medium bg-bg-main px-4 py-2 rounded-full border border-border-main shadow-sm">
                         {typeof pathInfo === 'string' ? pathInfo : pathInfo.title}
                       </span>
                       {idx < generatedBrief.suggestedLearningPath.length - 1 && (
                         <div className="text-indigo-400/50 hidden sm:block">→</div>
                       )}
                       {idx < generatedBrief.suggestedLearningPath.length - 1 && (
                         <div className="text-indigo-400/50 sm:hidden">↓</div>
                       )}
                     </React.Fragment>
                  ))}
               </div>
             </div>
          )}

          <div className="pt-8 border-t border-border-main flex flex-col sm:flex-row gap-4 items-center justify-center">
             {generatedCourse ? (
               <button
                 onClick={() => setGeneratedBrief(null)}
                 className="w-full sm:w-auto px-8 py-3 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-lg transition-transform hover:-translate-y-0.5"
               >
                 View Generated Course
               </button>
             ) : (
               <button
                 onClick={() => handleSubmit('course')}
                 className="w-full sm:w-auto px-8 py-3 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-lg transition-transform hover:-translate-y-0.5"
               >
                 Create Course from this Document
               </button>
             )}
             <button
               onClick={() => setGeneratedBrief(null)}
               className="w-full sm:w-auto px-8 py-3 text-sm font-medium text-text-main bg-bg-main hover:bg-bg-alt border border-border-main rounded-xl transition-colors"
             >
               Upload Another Document
             </button>
          </div>
        </div>
      </div>
    );
  }

  if (generatedCourse && !activeJobId && !isGenerating) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-main tracking-tight">
            {regeneratingCourseId ? 'Review Regeneration' : 'Review Knowledge Map'}
          </h1>
          <p className="mt-2 text-text-alt">
            {regeneratingCourseId 
              ? 'Preview the updated course content below. Your existing course will remain live until you publish these changes.' 
              : 'Approve or reject the synthesized course content before publishing to the library.'}
          </p>
        </div>

        {/* Source Processing Status Section */}
        <div className="mb-8 bg-bg-alt rounded-2xl border border-border-main overflow-hidden shadow-xl">
          <div className="bg-bg-main/50 px-6 py-4 border-b border-border-main">
            <h3 className="text-lg font-semibold text-text-main">Source Processing Status</h3>
          </div>
          <div className="p-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {toArray(generatedCourse.generationMetadata?.sourceStatuses).length > 0 ? (
                toArray(generatedCourse.generationMetadata?.sourceStatuses).map((source: any, sIdx: number) => (
                  <div key={sIdx} className="bg-bg-main/40 p-4 rounded-xl border border-border-main flex flex-col gap-3 text-text-main">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-bg-alt rounded-lg text-indigo-500">
                          {source.type === 'pdf' && <FileText className="h-5 w-5" />}
                          {source.type === 'word' && <FileCode className="h-5 w-5" />}
                          {source.type === 'url' && <Globe className="h-5 w-5" />}
                          {source.type === 'text' && <LayoutGrid className="h-5 w-5" />}
                        </div>
                        <div className="overflow-hidden">
                          <p className="text-sm font-medium text-text-main truncate max-w-[150px]" title={source.name}>
                            {source.name}
                          </p>
                          <p className="text-[10px] text-text-alt uppercase tracking-widest font-bold">
                            {source.type}
                          </p>
                        </div>
                      </div>
                      <div className="shrink-0">
                        {source.status === 'success' && <CheckCircle2 className="h-5 w-5 text-emerald-500" />}
                        {source.status === 'failed' && <XCircle className="h-5 w-5 text-rose-500" />}
                        {source.status === 'partial' && <AlertCircle className="h-5 w-5 text-amber-500" />}
                      </div>
                    </div>
                    
                    <div className="mt-auto space-y-2 pt-2 border-t border-border-main">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-text-alt uppercase font-bold tracking-tight">Ingestion</span>
                        <span className={`text-[10px] font-bold ${
                          source.ingestionStatus === 'success' ? 'text-emerald-500' : 'text-rose-500'
                        }`}>
                          {source.ingestionStatus === 'success' ? 'Loaded successfully' : 'Failed to load'}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-text-alt uppercase font-bold tracking-tight">Generation</span>
                        <span className={`text-[10px] font-bold ${
                          source.generationStatus === 'used_in_course' ? 'text-emerald-500' : 
                          source.generationStatus === 'not_used' ? 'text-rose-500' :
                          'text-text-alt'
                        }`}>
                          {source.generationStatus === 'used_in_course' ? 'Used in final course' : 
                           source.generationStatus === 'not_used' ? 'Not used' : 
                           'Pending'}
                        </span>
                      </div>

                      {source.type === 'pdf' && source.totalPages && (
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-text-alt uppercase font-bold tracking-tight">Pages</span>
                          <span className="text-[10px] text-text-alt font-medium opacity-80">
                            {source.processedPages}/{source.totalPages} processed
                            {source.skippedPages ? ` (${source.skippedPages} skipped)` : ''}
                          </span>
                        </div>
                      )}

                      {source.message && (
                        <p className="mt-2 text-[10px] text-rose-500/80 italic leading-tight bg-rose-500/5 p-1.5 rounded">
                          {getFriendlyErrorMessage(source.message)}
                        </p>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-full py-4 text-center text-text-alt text-sm">
                  No source status data available.
                </div>
              )}
            </div>
            
            {/* Warning if only one source succeeded but multiple were provided */}
            {((Array.isArray(generatedCourse.generationMetadata?.sourceStatuses) 
              ? generatedCourse.generationMetadata.sourceStatuses.filter((s: any) => s.status === 'success').length 
              : 0) < (generatedCourse.generationMetadata?.sourceCount || 0)) && (
              <div className="mt-6 flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-amber-500 text-xs">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>Note: Some sources failed to be fully synthesized. Review the content map below to ensure it meets your expectations.</span>
              </div>
            )}
          </div>
        </div>

        {generatedCourse.generationMetadata?.isPartial && (
          <div className={`mb-6 p-4 rounded-xl flex items-start gap-3 border ${
            generatedCourse.generationMetadata.failedLessonsCount > 0 
              ? 'bg-rose-500/10 border-rose-500/50' 
              : 'bg-indigo-500/10 border-indigo-500/50'
          }`}>
            <AlertCircle className={`h-5 w-5 shrink-0 mt-0.5 ${
              generatedCourse.generationMetadata.failedLessonsCount > 0 ? 'text-rose-500' : 'text-indigo-500'
            }`} />
            <div>
              <h4 className={`font-medium ${
                generatedCourse.generationMetadata.failedLessonsCount > 0 ? 'text-rose-500' : 'text-indigo-500'
              }`}>
                {generatedCourse.generationMetadata.failedLessonsCount > 0 
                  ? "Partial Generation" 
                  : "Content Finalization"}
              </h4>
              <p className="text-sm text-text-alt mt-1">
                {generatedCourse.generationMetadata.failedLessonsCount > 0 
                  ? `Some lesson content could not be finalized (${generatedCourse.generationMetadata.failedLessonsCount}/${generatedCourse.generationMetadata.totalLessonsCount} lessons failed). You can approve the course now, and we'll automatically retry these sections when you start learning.`
                  : "Great news! All lessons were successfully generated. Your course is ready to be published."}
              </p>
            </div>
          </div>
        )}
        
        {error && (
          <div className="mb-6 bg-rose-500/10 border border-rose-500/50 text-rose-500 px-4 py-3 rounded-lg text-sm">
            {getFriendlyErrorMessage(error)}
          </div>
        )}

        <div className="space-y-8 mb-8">
          {(['explorer', 'apprentice', 'master'] as const).map((level) => (
            <div key={level} className="bg-bg-alt rounded-2xl border border-border-main overflow-hidden shadow-lg">
              <div className="bg-bg-main/50 px-6 py-4 border-b border-border-main">
                <h3 className="text-xl font-semibold text-indigo-500 capitalize">{level} Level</h3>
              </div>
              <div className="p-6 space-y-6">
                {(() => {
                  const levelData = generatedCourse.levels?.[level] || generatedCourse[level];
                  const lessons = toArray(Array.isArray(levelData) ? levelData : (levelData && typeof levelData === 'object' && 'lessons' in levelData ? (levelData as any).lessons : []));
                  return lessons.map((lesson: any, idx: number) => (
                    <div key={idx} className="bg-bg-main/30 p-4 rounded-xl border border-border-main">
                      <h4 className="text-sm font-medium text-text-alt mb-2 uppercase tracking-wider opacity-60">
                        Lesson {idx + 1}{typeof lesson !== 'string' && (lesson.lesson_title || lesson.title) ? `: ${lesson.lesson_title || lesson.title}` : ''}
                      </h4>
                      <div className="prose prose-invert prose-sm max-w-none line-clamp-3 text-text-main">
                        <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                          {typeof lesson === 'string' 
                            ? lesson 
                            : (lesson.summary || (lesson.source_context ? lesson.source_context.substring(0, 200) + '...' : (lesson.beginner?.audio || 'No summary available.')))}
                        </Markdown>
                      </div>
                      
                      {lesson.visuals && lesson.visuals.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {lesson.visuals.map((v: any, vIdx: number) => (
                            <span key={vIdx} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-indigo-500/10 text-indigo-400 text-[10px] font-bold uppercase border border-indigo-500/20">
                              <Eye className="h-3 w-3" />
                              {v.type} (P{v.page_number})
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ));
                })()}
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-4 justify-end">
          <button
            onClick={handleReject}
            disabled={isPublishing}
            className="px-6 py-3 bg-bg-alt hover:bg-border-main text-text-alt font-medium rounded-xl transition-colors disabled:opacity-50"
          >
            {regeneratingCourseId ? 'Discard Changes' : 'Reject & Try Again'}
          </button>
          <button
            onClick={handleApprove}
            disabled={isPublishing}
            className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl transition-colors disabled:opacity-50 shadow-lg shadow-indigo-600/40"
          >
            {isPublishing ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <CheckCircle2 className="h-5 w-5" />
            )}
            {regeneratingCourseId ? 'Publish Updated Course' : 'Publish Course'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[calc(100vh-4rem)] bg-bg-main text-text-main">
      {/* Sidebar */}
      <div className="w-64 bg-bg-alt border-r border-border-main p-6 hidden md:block shrink-0">
        <div className="space-y-2">
          <button
            onClick={handleSwitchToCreate}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors ${
              activeTab === 'create' 
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' 
                : 'text-text-alt hover:bg-bg-main hover:text-text-main'
            }`}
          >
            <LayoutGrid className="h-5 w-5" />
            Create Course
          </button>
          
          <div className="pt-4 pb-2">
             <div className="text-xs font-bold text-text-alt uppercase tracking-wider px-4">Manage My Courses</div>
          </div>
          
          <button
            onClick={() => setActiveTab('manage')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors ${
              activeTab === 'manage' 
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' 
                : 'text-text-alt hover:bg-bg-main hover:text-text-main'
            }`}
          >
            <Settings className="h-5 w-5" />
            My Courses
          </button>

          <button
            onClick={() => navigate('/documents')}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors text-text-alt hover:bg-bg-main hover:text-text-main"
          >
            <BookOpen className="h-5 w-5" />
            My Documents
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 overflow-y-auto w-full">
        {/* Mobile Navigation */}
        <div className="md:hidden flex gap-2 mb-6 overflow-x-auto pb-2">
          <button
            onClick={handleSwitchToCreate}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              activeTab === 'create' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-bg-alt text-text-alt border border-border-main'
            }`}
          >
            <LayoutGrid className="h-4 w-4" />
            Create Course
          </button>
          <button
            onClick={() => setActiveTab('manage')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              activeTab === 'manage' 
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' 
                : 'bg-bg-alt text-text-alt hover:text-text-main'
            }`}
          >
            <Settings className="h-4 w-4" />
            My Courses
          </button>
          <button
            onClick={() => navigate('/documents')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors bg-bg-alt text-text-alt hover:text-text-main border border-border-main"
          >
            <BookOpen className="h-4 w-4" />
            My Documents
          </button>
        </div>

        {activeTab === 'create' ? (
          <>
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-text-main tracking-tight">Course Content</h1>
              <p className="mt-2 text-text-alt">Upload documents and links. Our AI Teaching Engine will turn them into a structured, 3-level course.</p>
            </div>

            <form onSubmit={(e) => handleSubmit('course', e)} className="space-y-8 bg-bg-alt p-8 rounded-2xl border border-border-main shadow-xl text-text-main">
              {error && (
                <div className="bg-rose-500/10 border border-rose-500/30 text-rose-500 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-text-alt mb-2 opacity-80">Course Title</label>
                  <input
                    type="text"
                    required
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="block w-full px-4 py-3 bg-bg-main border border-border-main rounded-xl text-text-main placeholder-text-alt/30 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                    placeholder="e.g., Advanced Machine Learning Concepts"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-alt mb-2 opacity-80">Description</label>
                  <textarea
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="block w-full px-4 py-3 bg-bg-main border border-border-main rounded-xl text-text-main placeholder-text-alt/30 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                    placeholder="Brief overview of the course..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-alt mb-2 opacity-80">Price ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    value={price}
                    onChange={(e) => setPrice(parseFloat(e.target.value) || 0)}
                    className="block w-full px-4 py-3 bg-bg-main border border-border-main rounded-xl text-text-main placeholder-text-alt/30 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                  />
                </div>
              </div>

              <div className="border-t border-border-main pt-8 space-y-6">
                <h3 className="text-lg font-medium text-text-main mb-4 flex items-center gap-2">
                  <Globe className="h-5 w-5 text-indigo-400" />
                  Visibility & Sharing
                </h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <button
                    type="button"
                    onClick={() => setVisibility('private')}
                    className={`flex flex-col items-start p-4 rounded-xl border transition-all ${
                      visibility === 'private' 
                        ? 'bg-indigo-600/10 border-indigo-600 ring-2 ring-indigo-600/20' 
                        : 'bg-bg-main border-border-main hover:border-text-alt/30'
                    }`}
                  >
                    <Lock className="w-5 h-5 text-text-alt mb-2" />
                    <span className="font-bold text-sm">Keep Private</span>
                    <span className="text-xs text-text-alt mt-1">Only you can access this course.</span>
                  </button>

                  <button
                    type="button"
                    disabled={memberships.length === 0}
                    onClick={() => {
                        setVisibility('organisation');
                        if (!targetOrgId && memberships.length > 0) {
                            setTargetOrgId(memberships[0].orgId);
                        }
                    }}
                    className={`flex flex-col items-start p-4 rounded-xl border transition-all ${
                      visibility === 'organisation' 
                        ? 'bg-indigo-600/10 border-indigo-600 ring-2 ring-indigo-600/20' 
                        : 'bg-bg-main border-border-main hover:border-text-alt/30'
                    } ${memberships.length === 0 ? 'opacity-50 cursor-not-allowed grayscale' : ''}`}
                  >
                    <Shapes className={`w-5 h-5 mb-2 ${visibility === 'organisation' ? 'text-indigo-400' : 'text-text-alt'}`} />
                    <span className="font-bold text-sm">Share with Organisation</span>
                    <span className="text-xs text-text-alt mt-1">
                      {memberships.length === 0 
                        ? 'Join an organisation to enable this.' 
                        : 'Only selected organisation members can access this course.'}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setVisibility('public')}
                    className={`flex flex-col items-start p-4 rounded-xl border transition-all ${
                      visibility === 'public' 
                        ? 'bg-indigo-600/10 border-indigo-600 ring-2 ring-indigo-600/20' 
                        : 'bg-bg-main border-border-main hover:border-text-alt/30'
                    }`}
                  >
                    <Globe className="w-5 h-5 text-text-alt mb-2" />
                    <span className="font-bold text-sm">Publish Publicly</span>
                    <span className="text-xs text-text-alt mt-1">This course can appear in the Course Library.</span>
                  </button>
                </div>

                {visibility === 'public' && (
                  <p className="text-xs text-text-alt opacity-70 italic pl-1">Public courses may appear in the Course Library.</p>
                )}

                {visibility === 'organisation' && memberships.length > 0 && (
                  <div className="space-y-2 pt-2 animate-in fade-in slide-in-from-top-2">
                    <label className="text-xs font-bold text-text-alt opacity-60 uppercase tracking-widest ml-1">Select Organisation</label>
                    <select
                      value={targetOrgId}
                      onChange={(e) => setTargetOrgId(e.target.value)}
                      className="block w-full px-4 py-3 bg-bg-main border border-border-main rounded-xl text-text-main focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                    >
                      {toArray(memberships).map((m: any) => (
                        <option key={m.orgId} value={m.orgId}>Organisation: {m.orgId}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="border-t border-border-main pt-8 space-y-6">
                <h3 className="text-lg font-medium text-text-main mb-4 flex items-center gap-2">
                  <LayoutGrid className="h-5 w-5 text-indigo-400" />
                  Course Progression
                </h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setProgressionMode('flexible')}
                    className={`flex flex-col items-start p-4 rounded-xl border transition-all ${
                      progressionMode === 'flexible' 
                        ? 'bg-indigo-600/10 border-indigo-600 ring-2 ring-indigo-600/20' 
                        : 'bg-bg-main border-border-main hover:border-text-alt/30'
                    }`}
                  >
                    <Zap className={`w-5 h-5 mb-2 ${progressionMode === 'flexible' ? 'text-indigo-400' : 'text-text-alt'}`} />
                    <span className="font-bold text-sm">Flexible</span>
                    <span className="text-xs text-text-alt mt-1 text-left">Learners can move between lessons freely. Knowledge checks are recommended but not required.</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setProgressionMode('locked')}
                    className={`flex flex-col items-start p-4 rounded-xl border transition-all ${
                      progressionMode === 'locked' 
                        ? 'bg-indigo-600/10 border-indigo-600 ring-2 ring-indigo-600/20' 
                        : 'bg-bg-main border-border-main hover:border-text-alt/30'
                    }`}
                  >
                    <Lock className={`w-5 h-5 mb-2 ${progressionMode === 'locked' ? 'text-indigo-400' : 'text-text-alt'}`} />
                    <span className="font-bold text-sm">Locked</span>
                    <span className="text-xs text-text-alt mt-1 text-left">Learners must complete and pass required knowledge checks before moving to the next stage.</span>
                  </button>
                </div>
              </div>

              {!fromDocumentId ? (
                <>
                  <div className="border-t border-border-main pt-8">
                    <h3 className="text-lg font-medium text-text-main mb-4 flex items-center gap-2">
                       <LinkIcon className="h-5 w-5 text-indigo-400" />
                       Source URLs
                    </h3>
                <div className="space-y-3">
                  {toArray(urls).map((url: any, index: number) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="url"
                        value={url}
                        onChange={(e) => handleUrlChange(index, e.target.value)}
                        className="block w-full px-4 py-2 bg-bg-main border border-border-main rounded-lg text-text-main placeholder-text-alt/30 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                        placeholder="https://example.com/article"
                      />
                      {urls.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveUrl(index)}
                          className="p-2 text-text-alt hover:text-red-400 hover:bg-bg-alt rounded-lg transition-colors"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={handleAddUrl}
                    className="inline-flex items-center gap-2 text-sm text-indigo-400 hover:text-indigo-300 font-medium transition-colors"
                  >
                    <Plus className="h-4 w-4" /> Add another URL
                  </button>
                </div>
              </div>

              <div className="border-t border-border-main pt-8">
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex gap-3 items-start mb-6">
                   <Info className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                   <div>
                     <p className="text-sm text-amber-500 font-bold mb-1">Lumina is currently in controlled beta.</p>
                     <p className="text-xs text-text-alt">Large document generation is limited during pilot testing. For more stable conversions, please use shorter documents.</p>
                   </div>
                </div>

                <h3 className="text-lg font-medium text-text-main mb-4 flex items-center gap-2">
                  <UploadCloud className="h-5 w-5 text-indigo-400" />
                  Source Documents
                </h3>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 flex flex-col gap-2">
                    <div className="p-4 bg-indigo-500/5 border border-indigo-500/10 rounded-xl space-y-2">
                      <p className="text-sm font-bold text-text-main">For best results, upload:</p>
                      <ul className="text-xs text-text-alt list-disc pl-5 space-y-1">
                        <li>Readable PDF files up to 75 pages or 25 MB</li>
                        <li>Word documents up to 25 MB</li>
                        <li>Web links where supported</li>
                      </ul>
                      <p className="text-xs text-indigo-400 mt-2 italic flex flex-col gap-1">
                        <span className="flex items-center gap-1"><Monitor className="w-3 h-3 flex-shrink-0" /> Course creation works best on desktop.</span>
                        <span>If you are using iPad or iPhone, download iCloud files locally before uploading. Large files, scanned PDFs, or files stored only in cloud placeholders may not process correctly in the current MVP.</span>
                      </p>
                    </div>
                    <label className="flex-1 flex flex-col items-center justify-center border-2 border-dashed border-border-main bg-bg-main/50 rounded-2xl p-10 hover:bg-bg-alt hover:border-indigo-500 transition-all cursor-pointer group active:scale-[0.98]">
                      <UploadCloud className="h-10 w-10 text-text-alt opacity-40 group-hover:text-indigo-400 mb-3 transition-colors" />
                      <span className="text-sm font-bold text-text-main">Tap to Upload</span>
                      <span className="text-xs text-text-alt mt-1 uppercase tracking-widest font-bold opacity-60">PDF, DOCX</span>
                      <input
                        type="file"
                        id="file-upload"
                        className="hidden"
                        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        multiple
                        onChange={handleFileUpload}
                      />
                    </label>
                  </div>
                </div>

                {pdfs.length > 0 && (
                  <>
                  <ul className="mt-4 space-y-2">
                    {toArray(pdfs).map((pdf: any, index: number) => (
                      <li key={index} className="flex items-center justify-between py-2 pl-3 pr-4 text-sm bg-bg-main rounded-lg border border-border-main">
                        <div className="flex items-center flex-1 w-0">
                          <CheckCircle2 className="flex-shrink-0 h-5 w-5 text-emerald-500" />
                          <span className="ml-2 flex-1 w-0 truncate text-text-alt">{pdf.name}</span>
                        </div>
                        <div className="ml-4 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => handleRemovePdf(index)}
                            className="font-medium text-red-400 hover:text-red-300 transition-colors"
                          >
                            Remove
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                  
                  {userIntent === 'professional' && !localStorage.getItem('lumina_tooltip_professional_brief') && (
                    <div className="mt-4 bg-indigo-500/10 border border-indigo-500/20 rounded-xl p-4 flex gap-3 items-start relative shadow-sm">
                      <button 
                        onClick={() => {
                          localStorage.setItem('lumina_tooltip_professional_brief', 'true');
                          // Force a rerender by toggling a dummy state, or just let it disappear on next render
                        }}
                        className="absolute top-2 right-2 text-indigo-400 hover:text-indigo-300"
                      >
                        <X className="h-4 w-4" />
                      </button>
                      <Info className="h-5 w-5 text-indigo-400 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-text-main font-medium mb-1">Your document is private.</p>
                        <p className="text-xs text-text-alt mb-2">Since you're learning from your own document, you can extract learning objectives or generate a brief without creating a full public course.</p>
                        <button
                          type="button"
                          onClick={() => {
                            localStorage.setItem('lumina_tooltip_professional_brief', 'true');
                            handleSubmit('both');
                          }}
                          className="text-xs font-bold bg-indigo-600 text-white px-3 py-1.5 rounded-lg hover:bg-indigo-700 transition"
                        >
                          Generate AI Course & Brief Now
                        </button>
                      </div>
                    </div>
                  )}
                  </>
                )}
              </div>
              </>
              ) : (
                <div className="border-t border-border-main pt-8">
                  <h3 className="text-lg font-medium text-text-main mb-4 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-emerald-500" />
                    Using Existing Document
                  </h3>
                  <div className="bg-bg-main p-6 rounded-2xl border border-border-main shadow-sm flex items-start gap-4">
                     <div className="h-12 w-12 bg-emerald-500/10 rounded-xl flex items-center justify-center border border-emerald-500/20 text-emerald-500 shrink-0">
                       <BookOpen className="h-6 w-6" />
                     </div>
                     <div className="flex-1 flex flex-col">
                        <span className="font-bold text-text-main text-lg">{title || 'Document Bundle'}</span>
                        <span className="text-text-alt text-sm mt-1">{existingSources.length} source file(s) attached</span>
                        <div className="mt-4 pt-3 border-t border-border-main/50 space-y-2">
                           {existingSources.map((s, idx) => (
                             <div key={idx} className="flex items-center gap-2 text-xs text-text-alt">
                                <FileText className="h-4 w-4 shrink-0" />
                                <span>{s.name} ({s.type})</span>
                             </div>
                           ))}
                        </div>
                     </div>
                     <button
                       type="button"
                       onClick={() => {
                          setFromDocumentId(null);
                          setExistingSources([]);
                          navigate('/courses/manage', { replace: true });
                       }}
                       className="p-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-500 rounded-lg transition-colors border border-rose-500/20 shrink-0"
                       title="Remove attached document bundle"
                     >
                       <X className="h-5 w-5" />
                     </button>
                  </div>
                </div>
              )}

              {activeJobId && (
                <div className="mt-8 animate-in fade-in zoom-in duration-300">
                  <div className="bg-bg-main border border-border-main rounded-2xl p-6 space-y-6 shadow-2xl relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 animate-shimmer" />
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-600/20 rounded-lg">
                          <Rocket className="h-5 w-5 text-indigo-400 animate-pulse" />
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-text-main flex items-center gap-2">
                            Processing Course Synthesis
                            <span className="bg-indigo-500/20 text-indigo-400 text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider border border-indigo-500/30">
                              {jobStatus?.status || 'queued'}
                            </span>
                          </h3>
                          <p className="text-xs text-text-alt opacity-60 mt-1">{jobStatus?.message || 'Waking up the workers...'}</p>
                          <p className="text-[11px] text-text-alt/70 mt-1">
                            {jobStatus?.currentStep ? `Step: ${jobStatus.currentStep}` : 'Step: queued'}
                            {jobStatus?.attemptCount ? ` • Attempt ${jobStatus.attemptCount}/${jobStatus.maxAttempts || 3}` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="text-xs font-mono text-indigo-400 font-bold bg-indigo-600/10 px-2 py-1 rounded">
                        {jobStatus?.progress || 0}%
                      </div>
                    </div>

                    <div className="w-full bg-bg-main rounded-full h-2.5 overflow-hidden border border-border-main/50">
                      <div 
                        className="bg-indigo-500 h-full transition-all duration-1000 ease-in-out" 
                        style={{ width: `${jobStatus?.progress || 0}%` }}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      {toArray(jobStatus?.sources).map((source: any, idx: number) => (
                        <div key={idx} className="flex items-center gap-2 bg-bg-main/40 p-2.5 rounded-xl border border-border-main/30">
                          <div className={`w-2 h-2 rounded-full ${
                            ['extracted', 'used_in_course'].includes(source.status) ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' :
                            ['failed', 'not_used'].includes(source.status) ? 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.5)]' :
                            source.status === 'weak_text' ? 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.5)]' :
                            'bg-indigo-500 animate-pulse'
                          }`} />
                          <span className="text-[10px] font-medium text-text-alt truncate max-w-[120px]" title={`${source.name} (${source.status})`}>
                            {source.name} <span className="opacity-70">({source.status})</span>
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="bg-indigo-500/10 border border-indigo-500/20 p-4 rounded-xl flex items-center gap-3">
                      <Loader2 className="h-4 w-4 text-indigo-500 animate-spin shrink-0" />
                      <p className="text-[11px] text-indigo-500 leading-relaxed font-medium">
                        Lumina is analyzing your sources and building the teaching framework.
                        <strong> You can leave this page</strong>; we'll notify you when it's ready for review.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-8 border-t border-border-main">
                {(isGenerating && !activeJobId) ? (
                  <ProgressPanel reports={progressReports} />
                ) : !activeJobId && (
                  <div className="space-y-6">
                    <div className="text-center">
                      <h3 className="text-xl font-bold text-text-main">What would you like Lumina to do with this?</h3>
                      <p className="text-sm text-text-alt mt-2">Choose how you want to process your uploaded content.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <button
                        type="button"
                        onClick={() => handleSubmit('brief')}
                        className="flex flex-col items-start text-left p-5 rounded-2xl bg-bg-main hover:bg-bg-alt border border-border-main hover:border-indigo-500/50 shadow-sm transition-all group"
                      >
                        <h4 className="text-base font-bold text-text-main group-hover:text-indigo-400 transition-colors">Generate Document Brief</h4>
                        <p className="text-xs text-text-alt mt-2 leading-relaxed opacity-80">Get a short summary, one page brief, key points, important terms, and action items before deciding what to do next.</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSubmit('course')}
                        className="flex flex-col items-start text-left p-5 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-600/20 transition-all"
                      >
                        <h4 className="text-base font-bold">Create Learning Course</h4>
                        <p className="text-xs opacity-90 mt-2 leading-relaxed">Turn this document into structured lessons, knowledge checks, AI tutor support, and learner progress tracking.</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSubmit('both')}
                        className="flex flex-col items-start text-left p-5 rounded-2xl bg-bg-main hover:bg-bg-alt border border-border-main hover:border-indigo-500/50 shadow-sm transition-all group"
                      >
                        <h4 className="text-base font-bold text-text-main group-hover:text-indigo-400 transition-colors">Do Both</h4>
                        <p className="text-xs text-text-alt mt-2 leading-relaxed opacity-80">First generate a brief, then create a learning course from the same document.</p>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </form>
          </>
        ) : (
          <>
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-text-main tracking-tight">Manage My Courses</h1>
              <p className="mt-2 text-text-alt">Edit or delete courses you have created.</p>
            </div>

            {error && (
              <div className="mb-6 bg-rose-500/10 border border-rose-500/50 text-rose-500 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {activeJobId && (
              <div className="mb-8 animate-in fade-in slide-in-from-top-4 duration-500">
                <div className="bg-bg-alt border border-indigo-500/30 rounded-2xl p-8 space-y-6 shadow-2xl relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-full h-1.5 bg-indigo-600 shadow-[0_0_15px_rgba(79,70,229,0.5)]" />
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-indigo-500/10 rounded-2xl border border-indigo-500/20">
                        <Loader2 className="h-6 w-6 text-indigo-500 animate-spin" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-text-main flex items-center gap-3">
                          Regenerating Course
                          <span className="bg-indigo-500/20 text-indigo-400 text-[12px] px-2 py-0.5 rounded-full uppercase tracking-wider border border-indigo-500/30">
                            {jobStatus?.status || 'queued'}
                          </span>
                        </h3>
                        <p className="text-sm text-text-alt mt-1">Process identified as Job #{activeJobId.slice(-6)}</p>
                      </div>
                    </div>
                    <div className="text-2xl font-black text-indigo-500 tabular-nums">
                      {jobStatus?.progress || 0}%
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-xs font-bold text-text-alt uppercase tracking-widest">
                      <span>{jobStatus?.message || 'Analyzing sources...'}</span>
                      <span>Estimated {100 - (jobStatus?.progress || 0)}% remaining</span>
                    </div>
                    <div className="w-full bg-bg-main rounded-full h-3 overflow-hidden border border-border-main shadow-inner">
                      <div 
                        className="bg-gradient-to-r from-indigo-600 to-indigo-400 h-full transition-all duration-1000 ease-out shadow-[0_0_10px_rgba(79,70,229,0.3)]" 
                        style={{ width: `${jobStatus?.progress || 0}%` }}
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {toArray(jobStatus?.sources).map((source: any, idx: number) => (
                      <div key={idx} className="flex items-center gap-2 bg-bg-main/50 px-3 py-1.5 rounded-full border border-border-main">
                        <div className={`w-2 h-2 rounded-full ${
                          ['extracted', 'used_in_course'].includes(source.status) ? 'bg-emerald-500' :
                          ['failed', 'not_used'].includes(source.status) ? 'bg-rose-500' :
                          source.status === 'weak_text' ? 'bg-amber-500' :
                          'bg-indigo-500 animate-pulse'
                        }`} />
                        <span className="text-[10px] font-bold text-text-alt uppercase">
                          {source.name} 
                          <span className="ml-1 opacity-70">({source.status})</span>
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="pt-4 border-t border-border-main">
                    <div className="flex items-center gap-3 text-amber-500 bg-amber-500/5 p-4 rounded-xl border border-amber-500/10">
                      <AlertCircle className="h-5 w-5 shrink-0" />
                      <p className="text-xs font-medium leading-relaxed">
                        Regeneration is running in the cloud. You can continue managing other courses or even close this tab. 
                        <strong> The existing version of this course remains active for learners until you approve these changes.</strong>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-6">
              {viewingProgressCourse ? (
                <div className="bg-bg-alt rounded-2xl border border-border-main p-6 shadow-xl animate-in fade-in slide-in-from-bottom-4">
                  <div className="flex justify-between items-center mb-6 border-b border-border-main pb-4">
                    <div>
                      <h3 className="text-xl font-bold text-text-main flex items-center gap-2">
                        <Activity className="h-5 w-5 text-emerald-500" />
                        Course Progress
                      </h3>
                      <p className="text-sm text-text-alt mt-1">
                        Viewing progress for <span className="font-semibold text-text-main">{viewingProgressCourse.title}</span>
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => {
                          const cols = [
                            { key: 'learnerEmail', label: 'Learner Email' },
                            { key: 'learnerId', label: 'Learner ID' },
                            { key: 'progressPercentage', label: 'Progress %' },
                            { key: 'completedLessons', label: 'Completed Lessons' },
                            { key: 'totalLessons', label: 'Total Lessons' },
                            { key: 'lastAccessed', label: 'Last Active' },
                            { key: 'courseCompleted', label: 'Course Completed' },
                            { key: 'finalAssessmentStatus', label: 'Final Assessment Status' }
                          ];
                          exportToCsv(`course-progress-${viewingProgressCourse.title.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`, learnerProgress, cols);
                        }}
                        className="px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 dark:bg-indigo-500/10 dark:hover:bg-indigo-500/20 dark:text-indigo-400 text-sm font-medium border border-indigo-500/20 rounded-lg transition-colors flex items-center gap-2"
                      >
                        <Download className="h-4 w-4" /> Export CSV
                      </button>
                      <button 
                        onClick={() => setViewingProgressCourse(null)}
                        className="px-4 py-2 bg-bg-main hover:bg-bg-main/80 text-text-main text-sm font-medium border border-border-main rounded-lg transition-colors flex items-center gap-2"
                      >
                        <X className="h-4 w-4" /> Close
                      </button>
                    </div>
                  </div>

                  {progressLoading ? (
                    <div className="flex flex-col items-center justify-center py-12 text-text-alt">
                      <Loader2 className="h-8 w-8 animate-spin mb-4" />
                      <p>Loading learner progress...</p>
                    </div>
                  ) : learnerProgress.length === 0 ? (
                    <div className="text-center py-16 bg-bg-main rounded-xl border border-dashed border-border-main">
                      <Users className="mx-auto h-12 w-12 text-text-alt opacity-20 mb-4" />
                      <h4 className="text-lg font-medium text-text-main mb-2">No learner progress yet</h4>
                      <p className="text-text-alt max-w-md mx-auto">
                        Progress will appear here once learners start this course.
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto rounded-xl border border-border-main">
                      <table className="w-full text-left border-collapse min-w-[800px]">
                        <thead>
                          <tr className="bg-bg-main border-b border-border-main text-xs font-semibold text-text-alt uppercase tracking-wider">
                            <th className="p-4">Learner</th>
                            <th className="p-4">Status</th>
                            <th className="p-4 w-1/4">Progress</th>
                            <th className="p-4">Quizzes</th>
                            <th className="p-4">Assessment</th>
                            <th className="p-4">Last Active</th>
                            <th className="p-4 text-right">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border-main bg-bg-alt text-sm">
                          {learnerProgress.map((prog) => {
                            const isCompleted = prog.courseCompleted === true;
                            const isStarted = prog.progressPercentage > 0 || prog.completedLessons > 0;
                            const status = isCompleted ? 'Completed' : (isStarted ? 'In Progress' : 'Not Started');
                            
                            return (
                              <tr key={prog.id} className="hover:bg-bg-main/30 transition-colors" data-clarity-mask="true">
                                <td className="p-4">
                                  <div className="font-semibold text-text-main">{prog.learnerEmail || 'Anonymous'}</div>
                                  <div className="text-[10px] text-text-alt font-mono truncate max-w-[120px]">UID: {prog.learnerId}</div>
                                </td>
                                <td className="p-4">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border
                                    ${isCompleted ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' 
                                      : isStarted ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' 
                                      : 'bg-bg-main text-text-alt border-border-main'}`}>
                                    {status}
                                  </span>
                                </td>
                                <td className="p-4">
                                  <div className="flex items-center gap-3">
                                    <div className="flex-1 h-2 min-w-[100px] bg-bg-main rounded-full overflow-hidden border border-border-main/50">
                                      <div 
                                        className={`h-full ${isCompleted ? 'bg-emerald-500' : 'bg-indigo-500'} transition-all`} 
                                        style={{ width: `${prog.progressPercentage || 0}%` }}
                                      ></div>
                                    </div>
                                    <div className="text-xs font-bold text-text-main w-10 text-right">
                                      {prog.progressPercentage || 0}%
                                    </div>
                                  </div>
                                  <div className="text-[10px] text-text-alt mt-1">
                                    {prog.completedLessons || 0} of {prog.totalLessons || '?'} lessons
                                  </div>
                                </td>
                                <td className="p-4">
                                  <div className="text-sm font-semibold">{prog.quizAttempts || 0}</div>
                                  <div className="text-[10px] text-text-alt">attempts</div>
                                </td>
                                <td className="p-4">
                                  {prog.finalAssessmentStatus ? (
                                    <span className={`text-xs font-medium ${prog.finalAssessmentStatus.toLowerCase() === 'passed' ? 'text-emerald-500' : 'text-amber-500'}`}>
                                      {prog.finalAssessmentStatus}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-text-alt italic">Not Started</span>
                                  )}
                                </td>
                                <td className="p-4 text-xs text-text-alt">
                                  {prog.lastAccessed?.toDate ? prog.lastAccessed.toDate().toLocaleDateString() : 'Unknown'}
                                </td>
                                <td className="p-4 text-right">
                                  <button
                                    onClick={() => {
                                      const cols = [
                                        { key: 'learnerEmail', label: 'Learner Email' },
                                        { key: 'learnerId', label: 'Learner ID' },
                                        { key: 'progressPercentage', label: 'Progress %' },
                                        { key: 'completedLessons', label: 'Completed Lessons' },
                                        { key: 'totalLessons', label: 'Total Lessons' },
                                        { key: 'lastAccessed', label: 'Last Active' },
                                        { key: 'courseCompleted', label: 'Course Completed' },
                                        { key: 'finalAssessmentStatus', label: 'Final Assessment Status' }
                                      ];
                                      exportToCsv(`learner-progress-${prog.learnerId}-${prog.courseId}-${new Date().toISOString().split('T')[0]}.csv`, [prog], cols);
                                    }}
                                    className="p-2 text-text-alt hover:text-indigo-500 hover:bg-bg-main rounded-lg transition-colors inline-block"
                                    title="Export Learner Progress"
                                  >
                                    <Download className="h-4 w-4" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : myCourses.length === 0 ? (
                <div className="text-center py-12 bg-bg-alt rounded-2xl border border-border-main">
                  <Settings className="mx-auto h-12 w-12 text-text-alt opacity-20 mb-4" />
                  <p className="text-lg text-text-alt">You haven't created any courses yet.</p>
                  <button 
                    onClick={handleSwitchToCreate}
                    className="mt-4 text-indigo-500 hover:text-indigo-400 font-medium"
                  >
                    Create your first course
                  </button>
                </div>
              ) : (
                toArray<Course>(myCourses).map(course => (
                  <div key={course.id} className="bg-bg-alt rounded-xl border border-border-main overflow-hidden shadow-lg hover:border-indigo-500/30 transition-all">
                    {editingCourse?.id === course.id ? (
                      <div className="p-6 space-y-4">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg font-semibold text-text-main">Edit Course</h3>
                          <button 
                            onClick={() => setEditingCourse(null)}
                            className="text-text-alt hover:text-text-main"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text-alt mb-1 opacity-60">Title</label>
                          <input
                            type="text"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            className="block w-full px-3 py-2 bg-bg-main border border-border-main rounded-lg text-text-main focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text-alt mb-1 opacity-60">Description</label>
                          <textarea
                            rows={3}
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            className="block w-full px-3 py-2 bg-bg-main border border-border-main rounded-lg text-text-main focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text-alt mb-1 opacity-60">Price ($)</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={editPrice}
                            onChange={(e) => setEditPrice(parseFloat(e.target.value) || 0)}
                            className="block w-full px-3 py-2 bg-bg-main border border-border-main rounded-lg text-text-main focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                        
                        <div className="pt-2">
                          <label className="block text-sm font-medium text-text-alt mb-2 opacity-60">Visibility</label>
                          <div className="flex gap-2">
                            {(['private', 'organisation', 'public'] as const).map((v) => (
                              <button
                                key={v}
                                type="button"
                                disabled={v === 'organisation' && memberships.length === 0}
                                onClick={() => {
                                  setEditVisibility(v);
                                  if (v === 'organisation' && !editOrgId && memberships.length > 0) {
                                    setEditOrgId(memberships[0].orgId);
                                  }
                                }}
                                className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-all border ${
                                  editVisibility === v
                                    ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-600/20'
                                    : 'bg-bg-main border-border-main text-text-alt hover:border-text-alt/30'
                                } ${v === 'organisation' && memberships.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                              >
                                {v.charAt(0) + v.slice(1)}
                              </button>
                            ))}
                          </div>
                          
                          {editVisibility === 'organisation' && memberships.length > 0 && (
                            <select
                              value={editOrgId}
                              onChange={(e) => setEditOrgId(e.target.value)}
                              className="mt-2 block w-full px-3 py-2 bg-bg-main border border-border-main rounded-lg text-text-main text-sm focus:ring-indigo-500"
                            >
                              {toArray(memberships).map((m: any) => (
                                <option key={m.orgId} value={m.orgId}>Org: {m.orgId}</option>
                              ))}
                            </select>
                          )}
                        </div>

                        {/* Edit Sources */}
                        <div className="space-y-4 pt-4 border-t border-border-main">
                          <h4 className="text-sm font-medium text-text-alt uppercase tracking-wider text-[10px] opacity-60">Update Sources</h4>
                          
                          <div className="space-y-2">
                            <label className="block text-xs font-medium text-text-alt/60 uppercase">External URLs</label>
                            {toArray(editUrls).map((url: any, index: number) => (
                              <div key={index} className="flex gap-2">
                                <div className="relative flex-1">
                                  <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-alt opacity-40" />
                                  <input
                                    type="url"
                                    value={url}
                                    onChange={(e) => handleEditUrlChange(index, e.target.value)}
                                    placeholder="https://"
                                    className="block w-full pl-10 pr-3 py-2 bg-bg-main border border-border-main rounded-lg text-text-main text-sm focus:ring-indigo-500"
                                  />
                                </div>
                                <button onClick={() => handleRemoveEditUrl(index)} className="p-2 text-text-alt hover:text-rose-500 transition-colors">
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                            <button onClick={handleAddEditUrl} className="flex items-center gap-2 text-xs text-indigo-500 hover:text-indigo-400 transition-colors font-medium">
                              <Plus className="h-4 w-4" /> Add another URL
                            </button>
                          </div>

                          <div className="space-y-2">
                            <label className="block text-xs font-medium text-text-alt/60 uppercase">Documents</label>
                            <div className="flex flex-wrap gap-2">
                                  {toArray(editingCourse.sources?.pdfs).map((pdf: any, idx: number) => (
                                <div key={`existing-${idx}`} className="flex items-center gap-2 px-3 py-1.5 bg-bg-main/50 border border-border-main rounded-lg text-xs text-text-alt">
                                  <LayoutGrid className="h-3 w-3 opacity-40" />
                                  {pdf.name}
                                </div>
                              ))}
                              {toArray(editPdfs).map((pdf: any, idx: number) => (
                                <div key={`new-${idx}`} className="flex items-center gap-2 px-3 py-1.5 bg-indigo-500/10 border border-indigo-500/30 rounded-lg text-xs text-indigo-500">
                                  <LayoutGrid className="h-3 w-3" />
                                  {pdf.name}
                                  <button onClick={() => handleRemoveEditPdf(idx)}>
                                    <X className="h-3 w-3 hover:text-rose-500" />
                                  </button>
                                </div>
                              ))}
                              <label className="flex items-center gap-2 px-3 py-1.5 bg-bg-main hover:bg-border-main border border-border-main rounded-lg text-xs text-text-alt cursor-pointer transition-colors">
                                <UploadCloud className="h-3 w-3" />
                                Upload More
                                <input type="file" multiple accept=".pdf" className="hidden" onChange={handleEditFileUpload} />
                              </label>
                            </div>
                          </div>
                          
                          <p className="text-[10px] text-text-alt/50 italic leading-tight">Note: Adding new sources will update the course's source list. To completely rebuild the course roadmap using new insights, use the button below.</p>
                        </div>
                        <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-border-main">
                          <button
                            onClick={() => setEditingCourse(null)}
                            className="px-4 py-2 text-sm font-medium text-text-alt hover:text-text-main transition-colors order-3 sm:order-1"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleRegenerateCourse}
                            className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-bold text-amber-500 bg-amber-500/10 hover:bg-amber-500/20 rounded-lg transition-all border border-amber-500/20 order-1 sm:order-2 shadow-sm"
                          >
                            <Zap className="h-4 w-4" />
                            Regenerate Content
                          </button>
                          <button
                            onClick={handleSaveEdit}
                            className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors order-2 sm:order-3 shadow-md"
                          >
                            <Save className="h-4 w-4" />
                            Save Changes
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="p-6">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex flex-col gap-1 text-text-main">
                            <h3 className="text-xl font-semibold">{course.title}</h3>
                            <div className="flex items-center gap-2">
                              {course.visibility === 'public' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 uppercase tracking-wider">
                                  <Globe className="h-2.5 w-2.5" /> Public
                                </span>
                              ) : course.visibility === 'organisation' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/10 text-amber-500 border border-amber-500/20 uppercase tracking-wider">
                                  <Shapes className="h-2.5 w-2.5" /> Organisation
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-bg-main text-text-alt border border-border-main uppercase tracking-wider">
                                  <Lock className="h-2.5 w-2.5" /> Private
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-500/10 text-indigo-500 border border-indigo-500/20 shrink-0">
                            ${(course.price || 0).toFixed(2)}
                          </span>
                        </div>
                        <p className="text-sm text-text-alt mb-4 line-clamp-2">{course.description}</p>
                        
                        <div className="flex items-center justify-between pt-4 border-t border-border-main">
                          <div className="text-xs text-text-alt italic opacity-60">
                            Created: {course.createdAt?.toDate ? course.createdAt.toDate().toLocaleDateString() : 'Recently'}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => loadCourseProgress(course)}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-emerald-500 hover:bg-emerald-500/10 rounded-lg transition-colors"
                            >
                              <Activity className="h-4 w-4" />
                              Progress
                            </button>
                            <button
                              onClick={() => handleStartEdit(course)}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-500 hover:bg-indigo-500/10 rounded-lg transition-colors"
                            >
                              <Edit2 className="h-4 w-4" />
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteCourse(course.id)}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-rose-500 hover:bg-rose-500/10 rounded-lg transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>

      {/* Confirmation Modal */}
      {confirmModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-bg-alt rounded-2xl shadow-2xl border border-border-main w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6">
              <h3 className="text-xl font-bold text-text-main mb-2">{confirmModal.title}</h3>
              <p className="text-text-alt">{confirmModal.message}</p>
            </div>
            <div className="px-6 py-4 bg-bg-main border-t border-border-main flex justify-end gap-3">
              <button
                onClick={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                className="px-4 py-2 text-sm font-medium text-text-alt hover:text-text-main transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmModal.onConfirm}
                className="px-4 py-2 text-sm font-bold text-white bg-rose-600 hover:bg-rose-700 rounded-xl transition-all shadow-lg shadow-rose-600/20"
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
